import { I18n } from "@assets/strings";
import React, { useEffect, useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import styled, { css } from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { Carousel } from "@domain/carousel/carousel";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { getDisplayedAuthor } from "@domain/lms/utils";
import { useModalTools } from "@ui/modals/modalContext";
import { useConfirmation } from "@ui/modals/useConfirmation";
import { createToast } from "@ui/toasts/utils";
import { FullScreenIcon } from "@ui/components/icons";
import { row } from "@ui/components/layout";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { MenuRow } from "@ui/components/menus/menuRow";
import { SimpleMenuContent } from "@ui/components/menus/simpleMenuContent";
import { EditReferencePostModal } from "@ui/components/post/reference/editReferencePostModal";

interface CarouselThumbnailMenuProps extends React.HTMLAttributes<HTMLDivElement> {
	carousel: Carousel;
	fullScreenSetter: () => void;
	fullScreenTop?: boolean;
}

export const CarouselThumbnailMenu: React.FC<CarouselThumbnailMenuProps> = ({
	carousel,
	fullScreenSetter,
	fullScreenTop,
	...htmlProps
}) => {
	const [fullScreen, setFullScreen] = useState<boolean>();
	const { carouselService, trackingService } = useServices();

	useEffect(() => {
		if (fullScreen) {
			fullScreenSetter();
		}
		if (!fullScreenTop) setFullScreen(false);
	}, [fullScreen, fullScreenTop]);

	const currentProfile = useCurrentProfile();
	const displayedAuthor = getDisplayedAuthor(carousel);
	const subAuthor = displayedAuthor !== carousel.author ? carousel.author : undefined;
	const isAuthor = displayedAuthor.id === currentProfile?.id || subAuthor?.id === currentProfile?.id;

	const [favorite, setFavorite] = useState(carousel.isFavorite);
	const { createModal } = useModalTools();

	const askDeleteConfirmation = useConfirmation(
		I18n.t("carousel.delete.title"),
		I18n.t("delete"),
		I18n.t("carousel.delete.message")
	);

	const history = useHistory();

	return (
		<KebabMenu authOnly {...htmlProps}>
			<SimpleMenuContent
				reportInfos={
					isAuthor
						? undefined
						: {
								contentId: carousel.id,
								contentType: "CAROUSEL",
							}
				}
				onFavorite={async () => {
					await carouselService.toggleFavorite({ ...carousel, isFavorite: favorite });
					setFavorite(!favorite);
					trackingService.track("Carousel - bookmarking");
				}}
				favorite={favorite}
				onShare={() => {
					createModal((close) => (
						<EditReferencePostModal close={close} shared={{ type: "SHARE_CAROUSEL", reference: carousel }} />
					));
				}}
				onDelete={
					isAuthor
						? () => {
								askDeleteConfirmation(async () => {
									await carouselService.deleteCarousel(carousel.id);
									createToast(
										I18n.t("carousel.toast.delete.title"),
										I18n.t("carousel.toast.delete.message"),
										"success"
									);
									history.push(Path.Root);
								});
							}
						: undefined
				}
			>
				<MenuRow onClick={() => setFullScreen(!fullScreen)}>
					<IconWrapper doNotFill>
						<FullScreenIcon fillSquare={"#206AF8"} />
					</IconWrapper>
					<span>{I18n.t("carousel.options.setFullScreen")}</span>
				</MenuRow>
			</SimpleMenuContent>
		</KebabMenu>
	);
};

// const IconWrapper = styled.div`
// 	flex-shrink: 0;
// 	border-radius: 9px;
// 	width: 34px;
// 	height: 34px;
// 	background-color: ${({ theme }) => theme.colors.secondaryLight};
// 	${row(0, "center", "center")};

// 	> svg path {
// 		fill: ${({ theme }) => theme.colors.secondary};
// 	}
// `;

const IconWrapper = styled.div.withConfig({
	shouldForwardProp: (prop) => !["primaryColor", "doNotFill"].includes(prop),
})<{ primaryColor?: boolean; doNotFill?: boolean }>`
	flex-shrink: 0;
	border-radius: 9px;
	width: 34px;
	height: 34px;
	background-color: ${({ theme, primaryColor }) =>
		primaryColor ? theme.colors.primaryLight : theme.colors.secondaryLight};
	${row(0, "center", "center")};
	${({ primaryColor, doNotFill }) =>
		primaryColor
			? css`
					> svg .main-path {
						fill: ${({ theme }) => theme.colors.primary};
					}
				`
			: !doNotFill
				? css`
						> svg path {
							fill: ${({ theme }) => theme.colors.secondary};
						}
					`
				: undefined};
`;
